import type { MutationResult } from '../../api/contract';
import { createAppError } from '../../api/errors';
import { gameFixtures, gameTypeFixtures, partnerGameFixtures } from './fixtures';
import type { GamesRepository } from './repository';
import type {
  GameDetail,
  GameListQuery,
  GameTypeDetail,
  GameTypeListQuery,
  MasterSort,
  PartnerGame,
} from './types';
import { normalizeMasterCode } from './types';

export interface MockGamesRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
  readonly partnerGameConflict?: boolean;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const gameTypeDetailAliases: Readonly<Record<string, string>> = {
  'game-type-a': gameTypeFixtures[0]!.id,
};
const gameDetailAliases: Readonly<Record<string, string>> = {
  'game-a': gameFixtures[0]!.id,
};

export class MockGamesRepository implements GamesRepository {
  readonly #mode: 'data' | 'empty' | 'error';
  readonly #partnerGameConflict: boolean;
  #gameTypes: GameTypeDetail[];
  #games: GameDetail[];
  #partnerGames: PartnerGame[];

  constructor(options: MockGamesRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
    this.#partnerGameConflict = options.partnerGameConflict ?? false;
    this.#gameTypes = this.#mode === 'empty' ? [] : gameTypeFixtures.map(cloneGameType);
    this.#games = this.#mode === 'empty' ? [] : gameFixtures.map(cloneGame);
    this.#partnerGames = this.#mode === 'empty' ? [] : partnerGameFixtures.map(clonePartnerGame);
  }

  async listGameTypes(query: GameTypeListQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    const rows = this.#gameTypes
      .filter((item) => matchesMaster(item, query.filters))
      .sort((left, right) => compareMaster(left, right, query.sort, query.order));
    return page(rows, query.page, query.pageSize, 'mock-game-types-list', cloneGameType);
  }

  async getGameType(gameTypeId: string, signal: AbortSignal): Promise<GameTypeDetail> {
    this.#assertAvailable(signal);
    return cloneGameType(this.#findGameType(gameTypeId));
  }

  async createGameType(input: Parameters<GamesRepository['createGameType']>[0]) {
    this.#assertAvailable();
    const code = normalizeMasterCode(input.code);
    this.#assertUnique(this.#gameTypes, code, 'Game Type');
    const created: GameTypeDetail = {
      id: mockUuid(920 + this.#gameTypes.length),
      code,
      name: input.name.trim(),
      status: 'SUSPENDED',
      effectiveStatus: 'SUSPENDED',
      dependencyBlockers: [],
      ...timestamps('2026-07-23T12:00:00+07:00'),
    };
    this.#gameTypes = [created, ...this.#gameTypes];
    return Promise.resolve(result(cloneGameType(created), 'mock-game-type-create'));
  }

  async updateGameType(
    gameTypeId: string,
    input: Parameters<GamesRepository['updateGameType']>[1],
  ) {
    this.#assertAvailable();
    const current = this.#findGameType(gameTypeId);
    const updated = {
      ...current,
      name: input.name.trim(),
      ...updatedAt('2026-07-23T12:10:00+07:00'),
    };
    this.#gameTypes = replace(this.#gameTypes, updated);
    return Promise.resolve(result(cloneGameType(updated), 'mock-game-type-update'));
  }

  async changeGameTypeStatus(
    gameTypeId: string,
    input: Parameters<GamesRepository['changeGameTypeStatus']>[1],
  ) {
    this.#assertAvailable();
    const current = this.#findGameType(gameTypeId);
    assertNotReopened(current.status, input.status, 'Game Type');
    const updated: GameTypeDetail = {
      ...current,
      status: input.status,
      effectiveStatus: input.status,
      ...updatedAt('2026-07-23T12:20:00+07:00'),
    };
    this.#gameTypes = replace(this.#gameTypes, updated);
    this.#games = this.#games.map((item) =>
      item.gameType.id === updated.id
        ? {
            ...item,
            gameType: {
              id: updated.id,
              code: updated.code,
              name: updated.name,
              status: updated.status,
            },
            effectiveStatus: updated.status === 'ACTIVE' ? item.status : updated.status,
          }
        : item,
    );
    return Promise.resolve(result(cloneGameType(updated), 'mock-game-type-status'));
  }

  async listGames(query: GameListQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    const rows = this.#games
      .filter(
        (item) =>
          (query.filters.gameTypeId === '' || item.gameType.id === query.filters.gameTypeId) &&
          matchesMaster(item, query.filters),
      )
      .sort((left, right) => compareMaster(left, right, query.sort, query.order));
    return page(rows, query.page, query.pageSize, 'mock-games-list', cloneGame);
  }

  async getGame(gameId: string, signal: AbortSignal): Promise<GameDetail> {
    this.#assertAvailable(signal);
    return cloneGame(this.#findGame(gameId));
  }

  async createGame(input: Parameters<GamesRepository['createGame']>[0]) {
    this.#assertAvailable();
    const gameType = this.#findGameType(input.gameTypeId);
    const code = normalizeMasterCode(input.code);
    this.#assertUnique(this.#games, code, 'Game');
    const created: GameDetail = {
      id: mockUuid(930 + this.#games.length),
      gameType: {
        id: gameType.id,
        code: gameType.code,
        name: gameType.name,
        status: gameType.status,
      },
      code,
      name: input.name.trim(),
      status: 'SUSPENDED',
      effectiveStatus: 'SUSPENDED',
      dependencyBlockers: gameType.status === 'ACTIVE' ? [] : ['GAME_TYPE_NOT_ACTIVE'],
      ...timestamps('2026-07-23T12:30:00+07:00'),
    };
    this.#games = [created, ...this.#games];
    return Promise.resolve(result(cloneGame(created), 'mock-game-create'));
  }

  async updateGame(gameId: string, input: Parameters<GamesRepository['updateGame']>[1]) {
    this.#assertAvailable();
    const current = this.#findGame(gameId);
    const updated = {
      ...current,
      name: input.name.trim(),
      ...updatedAt('2026-07-23T12:40:00+07:00'),
    };
    this.#games = replace(this.#games, updated);
    return Promise.resolve(result(cloneGame(updated), 'mock-game-update'));
  }

  async changeGameStatus(
    gameId: string,
    input: Parameters<GamesRepository['changeGameStatus']>[1],
  ) {
    this.#assertAvailable();
    const current = this.#findGame(gameId);
    assertNotReopened(current.status, input.status, 'Game');
    if (input.status === 'ACTIVE' && current.gameType.status !== 'ACTIVE') {
      return Promise.reject(
        conflict('Activation requires an active Game Type', 'mock-game-dependency'),
      );
    }
    const updated: GameDetail = {
      ...current,
      status: input.status,
      effectiveStatus: input.status,
      dependencyBlockers: input.status === 'ACTIVE' ? [] : current.dependencyBlockers,
      ...updatedAt('2026-07-23T12:50:00+07:00'),
    };
    this.#games = replace(this.#games, updated);
    return Promise.resolve(result(cloneGame(updated), 'mock-game-status'));
  }

  async listPartnerGames(partnerId: string, signal: AbortSignal) {
    this.#assertAvailable(signal);
    return this.#partnerGames.filter((item) => item.partnerId === partnerId).map(clonePartnerGame);
  }

  async getPartnerGame(
    partnerId: string,
    gameId: string,
    signal: AbortSignal,
  ): Promise<PartnerGame> {
    this.#assertAvailable(signal);
    return clonePartnerGame(this.#findPartnerGame(partnerId, gameId));
  }

  async createPartnerGame(
    partnerId: string,
    input: Parameters<GamesRepository['createPartnerGame']>[1],
  ) {
    this.#assertAvailable();
    if (
      this.#partnerGames.some(
        (item) => item.partnerId === partnerId && item.game.id === input.gameId,
      )
    ) {
      throw conflict(
        'This Game is already configured for the Partner',
        'mock-partner-game-duplicate',
      );
    }
    const game = this.#findGame(input.gameId);
    const created: PartnerGame = {
      partnerId,
      game: cloneGame(game),
      status: 'SUSPENDED',
      effectiveStatus: 'SUSPENDED',
      dependencyBlockers: game.effectiveStatus === 'ACTIVE' ? [] : ['GAME_OR_GAME_TYPE_NOT_ACTIVE'],
      currencyCode: 'THB',
      maximumPlayAmount: input.maximumPlayAmount,
      ...timestamps('2026-07-23T13:00:00+07:00'),
    };
    this.#partnerGames = [created, ...this.#partnerGames];
    return result(clonePartnerGame(created), 'mock-partner-game-create');
  }

  async updatePartnerGame(
    partnerId: string,
    gameId: string,
    input: Parameters<GamesRepository['updatePartnerGame']>[2],
  ) {
    this.#assertAvailable();
    const current = this.#findPartnerGame(partnerId, gameId);
    if (this.#partnerGameConflict) {
      throw createAppError({
        status: 409,
        code: 'PARTNER_GAME_CURRENCY_CONFLICT',
        message: 'Currency conflict: Partner default currency changed',
        requestId: 'mock-partner-game-currency-conflict',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
    const updated: PartnerGame = {
      ...current,
      maximumPlayAmount: input.maximumPlayAmount,
      ...updatedAt('2026-07-23T13:10:00+07:00'),
    };
    this.#partnerGames = replacePartnerGame(this.#partnerGames, updated);
    return result(clonePartnerGame(updated), 'mock-partner-game-update');
  }

  async changePartnerGameStatus(
    partnerId: string,
    gameId: string,
    input: Parameters<GamesRepository['changePartnerGameStatus']>[2],
  ) {
    this.#assertAvailable();
    const current = this.#findPartnerGame(partnerId, gameId);
    if (
      input.status === 'ACTIVE' &&
      (current.game.effectiveStatus !== 'ACTIVE' || current.dependencyBlockers.length > 0)
    ) {
      throw conflict(
        'Partner game activation dependencies are not satisfied',
        'mock-partner-game-dependency',
      );
    }
    const updated: PartnerGame = {
      ...current,
      status: input.status,
      effectiveStatus: input.status,
      ...updatedAt('2026-07-23T13:20:00+07:00'),
    };
    this.#partnerGames = replacePartnerGame(this.#partnerGames, updated);
    return result(clonePartnerGame(updated), 'mock-partner-game-status');
  }

  #assertAvailable(signal?: AbortSignal): void {
    if (signal?.aborted === true) throw new DOMException('Game request aborted', 'AbortError');
    if (this.#mode === 'error') {
      throw createAppError({
        status: 503,
        code: 'MOCK_GAMES_UNAVAILABLE',
        message: 'Game fixture is unavailable',
        requestId: 'mock-games-error',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
  }

  #findGameType(id: string): GameTypeDetail {
    const resolvedId = gameTypeDetailAliases[id] ?? id;
    const item = this.#gameTypes.find((candidate) => candidate.id === resolvedId);
    if (item === undefined) throw notFound('Game Type', 'mock-game-type-not-found');
    return item;
  }

  #findGame(id: string): GameDetail {
    const resolvedId = gameDetailAliases[id] ?? id;
    const item = this.#games.find((candidate) => candidate.id === resolvedId);
    if (item === undefined) throw notFound('Game', 'mock-game-not-found');
    return item;
  }

  #findPartnerGame(partnerId: string, gameId: string): PartnerGame {
    const item = this.#partnerGames.find(
      (candidate) => candidate.partnerId === partnerId && candidate.game.id === gameId,
    );
    if (item === undefined) throw notFound('Partner game', 'mock-partner-game-not-found');
    return item;
  }

  #assertUnique(items: readonly { readonly code: string }[], code: string, label: string): void {
    if (items.some((item) => item.code === code))
      throw conflict(`${label} code already exists`, 'mock-game-code-conflict');
  }
}

function matchesMaster(
  item: { readonly code: string; readonly name: string; readonly status: string },
  filters: { readonly code: string; readonly name: string; readonly status: string },
): boolean {
  return (
    includes(item.code, filters.code) &&
    includes(item.name, filters.name) &&
    (filters.status === '' || item.status === filters.status)
  );
}

function compareMaster<T extends Record<MasterSort, string>>(
  left: T,
  right: T,
  sort: MasterSort,
  order: 'asc' | 'desc',
): number {
  const comparison = left[sort].localeCompare(right[sort]);
  return order === 'asc' ? comparison : -comparison;
}

function includes(value: string, search: string): boolean {
  return value.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
}

function page<T>(
  items: readonly T[],
  pageNumber: number,
  pageSize: number,
  requestId: string,
  clone: (item: T) => T,
) {
  const start = Math.max(0, pageNumber - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize).map(clone),
    total: items.length,
    requestId,
  };
}

function cloneGameType(item: GameTypeDetail): GameTypeDetail {
  return { ...item, dependencyBlockers: [...item.dependencyBlockers] };
}

function cloneGame(item: GameDetail): GameDetail {
  return {
    ...item,
    gameType: { ...item.gameType },
    dependencyBlockers: [...item.dependencyBlockers],
  };
}

function clonePartnerGame(item: PartnerGame): PartnerGame {
  return {
    ...item,
    game: cloneGame(item.game),
    dependencyBlockers: [...item.dependencyBlockers],
  };
}

function replace<T extends { readonly id: string }>(items: readonly T[], updated: T): T[] {
  return items.map((item) => (item.id === updated.id ? updated : item));
}

function replacePartnerGame(items: readonly PartnerGame[], updated: PartnerGame): PartnerGame[] {
  return items.map((item) =>
    item.partnerId === updated.partnerId && item.game.id === updated.game.id ? updated : item,
  );
}

function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data, requestId };
}

function timestamps(timestamp: string) {
  return { createdAt: timestamp, createdBy: actorId, updatedAt: timestamp, updatedBy: actorId };
}

function updatedAt(timestamp: string) {
  return { updatedAt: timestamp, updatedBy: actorId };
}

function assertNotReopened(current: string, next: string, label: string): void {
  if (current === 'CLOSED' && next !== 'CLOSED')
    throw conflict(`Closed ${label}s cannot be reopened`, 'mock-game-terminal');
}

function conflict(message: string, requestId: string) {
  return createAppError({
    status: 409,
    code: 'GAME_CONFLICT',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function notFound(label: string, requestId: string) {
  return createAppError({
    status: 404,
    code: 'GAME_NOT_FOUND',
    message: `${label} not found`,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

const actorId = '00000000-0000-4000-8000-000000000001';

function mockUuid(sequence: number): string {
  return `00000000-0000-4000-8000-${String(sequence).padStart(12, '0')}`;
}
